import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { States } from '../../../constants';
import { constructionType } from '../../../constants/Project';
import { useOrgTitles } from '../../../hooks/ReactQueryHooks/useTitlesQuery';
import { useUsers } from '../../../hooks/ReactQueryHooks/useUsersQuery';
import { usePermission } from '../../../hooks/usePermission';
import type { ReactSelectOption } from '../../../types/Constants';
import type { FiltersInterface } from '../../../types/Miscellaneous';
import Button from '../../UI/Button';
import { HookForm } from '../../UI/HookForm/Form';
import { HookReactSingleSelect } from '../../UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '../../UI/HookForm/Provider';
import Modal from '../../UI/Modal';

interface IProjectFilterModalData {
  name?: string;
  constructionType?: ReactSelectOption;
  state?: ReactSelectOption;
  leadId?: ReactSelectOption;
  memberId?: ReactSelectOption;
  titleId?: ReactSelectOption;
  neighborhood?: string;
  archived?: ReactSelectOption;
  departmentLedProject?: ReactSelectOption;
}

export default function ProjectFilterModal({
  handleModal,
  applyFiltersHandler,
  appliedFilters,
}: {
  handleModal: () => void;
  applyFiltersHandler: (filters: FiltersInterface, displayFilters: Record<string, any>) => void;
  appliedFilters: FiltersInterface;
}) {
  const { hasPermission } = usePermission();

  const fetchOrganizationTitles = useOrgTitles({
    params: { active: 'true' },
  });

  const fetchUsers = useUsers({
    params: { active: 'true' },
  });

  const users = fetchUsers.data?.data;

  const organizationTitle = fetchOrganizationTitles.data?.data;

  const validProjectLeadOptions = users?.map((u: any) => ({
    label: u.name,
    value: u.id,
  }));

  const projectMemberOptions = users?.map((u: any) => ({
    label: u.name,
    value: u.id,
  }));

  const organizationTitleOptions = organizationTitle?.map((u: any) => ({
    label: u.name,
    value: u.id,
  }));

  const optionFor = (key: keyof IProjectFilterModalData) => {
    const value = appliedFilters[key];
    return value === undefined ? undefined : { label: String(value), value: String(value) };
  };

  const defaultValues: IProjectFilterModalData = {
    name: typeof appliedFilters.name === 'string' ? appliedFilters.name : undefined,
    constructionType: optionFor('constructionType'),
    state: optionFor('state'),
    leadId: optionFor('leadId'),
    memberId: optionFor('memberId'),
    titleId: optionFor('titleId'),
    neighborhood: typeof appliedFilters.neighborhood === 'string' ? appliedFilters.neighborhood : undefined,
    archived: optionFor('archived'),
    departmentLedProject: optionFor('departmentLedProject'),
  };

  const filterSubmitHandler = (formData: IProjectFilterModalData) => {
    const selectedFilters: FiltersInterface = {};

    Object.keys(formData).forEach((k: string) => {
      let selectedValue = formData[k as keyof Omit<IProjectFilterModalData, 'name' | 'neighborhood'>]?.value;

      if (k === 'name' || k === 'neighborhood') {
        selectedValue = formData[k];
      }

      if (selectedValue) {
        selectedFilters[k] = selectedValue;
      }
    });

    applyFiltersHandler(selectedFilters, {
      ...(formData?.name && {
        name: {
          label: 'Project Name',
          value: formData?.name,
        },
      }),
      ...(formData?.constructionType && {
        constructionType: {
          label: 'Construction Type',
          value: formData?.constructionType?.label,
        },
      }),
      ...(formData?.state && {
        state: {
          label: 'State',
          value: formData?.state?.label,
        },
      }),
      ...(formData?.titleId && {
        titleId: {
          label: 'Organization Title',
          value: formData?.titleId?.label,
        },
      }),
      ...(formData?.leadId && {
        leadId: {
          label: 'Project Lead',
          value: formData?.leadId?.label,
        },
      }),
      ...(formData?.memberId && {
        memberId: {
          label: 'Project member',
          value: formData?.memberId?.label,
        },
      }),
      ...(formData?.neighborhood && {
        neighborhood: {
          label: 'Neighborhood',
          value: formData?.neighborhood,
        },
      }),
      ...(formData?.archived && {
        archived: {
          label: 'Archived',
          value: formData?.archived?.label,
        },
      }),
      ...(formData?.departmentLedProject && {
        departmentLedProject: {
          label: 'Department Led Project',
          value: formData?.departmentLedProject?.label,
        },
      }),
    });
    handleModal();
  };

  return (
    <Modal>
      <HookFormProvider defaultValues={defaultValues}>
        <HookForm onSubmit={filterSubmitHandler}>
          <h2 className='text-primaryColor-normal mt-0'>Project Filter</h2>
          <section className='space-y-5'>
            <HookInput customClassName='grid grid-cols-2' label='Project Name' name='name' type='text' />
            <HookReactSingleSelect
              label='Construction Type'
              name='constructionType'
              componentClassName='grid grid-cols-2'
              options={constructionType}
            />
            <HookReactSingleSelect
              label='State'
              name='state'
              componentClassName='grid grid-cols-2'
              options={States.map((s) => ({
                label: s.name,
                value: s.name,
              }))}
            />
            <HookReactSingleSelect
              label='Organization Title'
              name='titleId'
              componentClassName='grid grid-cols-2'
              options={organizationTitleOptions}
            />
            <HookReactSingleSelect
              label='Project Lead'
              name='leadId'
              componentClassName='grid grid-cols-2'
              options={validProjectLeadOptions}
            />

            <HookReactSingleSelect
              label='Project member'
              name='memberId'
              componentClassName='grid grid-cols-2'
              options={projectMemberOptions}
            />

            {/* <HookReactSingleSelect
              label="Neighborhood"
              name="neighborhood"
              componentClassName="grid grid-cols-2"
              options={neighborHoods.map((s) => ({
                label: s.name,
                value: s.name,
              }))}
            /> */}
            <HookInput customClassName='grid grid-cols-2' label='Neighborhood' name='neighborhood' type='text' />
            {hasPermission(['LIST_ALL_PROJECTS']) && (
              <HookReactSingleSelect
                label='Archived Project'
                name='archived'
                componentClassName='grid grid-cols-2'
                options={[{ label: 'True', value: 'true' }]}
              />
            )}
            <HookReactSingleSelect
              label='Department Led Project'
              name='departmentLedProject'
              componentClassName='grid grid-cols-2'
              options={[{ label: 'True', value: 'TRUE' }]}
            />
            <div className='flex flex-row mt-8 w-full justify-end items-center space-x-5'>
              <Button label='Cancel' btnStyle='btn-neutral' btnType='button' onClick={handleModal} />
              <Button label='Apply' btnStyle='btn-primary' btnType='Submit' />
            </div>
          </section>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
}
